// The Management cross-roster home — the "Roster heartbeat". The Management analogue of
// the Label RosterHomeScreen: a Needs-Attention triage (overdue tasks / expiring holds / active rollouts / new
// opportunities) + the Roster pulse + Tasks-this-week + an Upcoming-events strip (next 14 days). MONEY-FREE by
// construction — the composed reads carry no money field; the FE renders none. Four-state contract
// (loading skeleton / error+retry / born-blank / content); an unassigned staffer (artistCount 0) sees the
// designed empty rail, never fabricated numbers.
import { Users } from "lucide-react";
import { AsOfMarker, Button, Card, Icon, StatDelta, compactNumber } from "@cntrl/ui";
import { useOrgContext, useOrgCapability } from "../../app/orgContext.js";
import { openTeamPanel } from "../../app/teamPanel.js";
import { useRosterOverview, useRosterNeedsAttention, useRosterEventsFeed } from "../../app/hooks/useRoster.js";
import { NeedsAttentionCard } from "./NeedsAttentionCard.js";
import { RosterScreenShell, type RosterScreenState } from "./RosterScreenShell.js";
import styles from "./RosterHeartbeatScreen.module.scss";
import { formatDay } from "./rosterFormat.js";
import { TaskListRow } from "../tasks/TaskListRow.js";
import { useDirectoryPeople } from "../tasks/taskPeople.js";
import { asTaskSummary, sortTasksForList, taskRowsClassName } from "../tasks/taskRow.js";
import { ArtistMark } from "./ArtistMark.js";

/** The header's roster line — legacy's "3 artists managed", pluralized. */
function managedLine(count: number): string {
  return `${count} ${count === 1 ? "artist" : "artists"} managed`;
}

// NO header action (PO, 2026-09-02). It carried "+ New task", which could only send you to the
// artist-required gate — task create is band-scoped, so the cross-roster page has nothing to create ON.
// The action lives on the PER-ARTIST page now, where the band is known and it opens the real modal.
export function RosterHeartbeatScreen({
  onNavigate,
  onOpenTask,
}: {
  onNavigate?: (route: string) => void;
  onOpenTask?: (bandId: string, taskId: string) => void;
}) {
  const { orgId } = useOrgContext();
  const canManageTeam = useOrgCapability("roster:manage_seats");
  const overview = useRosterOverview(orgId);
  const attention = useRosterNeedsAttention(orgId);
  const events = useRosterEventsFeed(orgId);

  const data = overview.data;
  const artistCount = data?.header?.artistCount ?? 0;
  const pulse = data?.pulse ?? [];
  const tasks = data?.tasks;
  const taskPeople = useDirectoryPeople(tasks?.people ?? undefined);
  const artistNameOf = new Map((tasks?.items ?? []).map((t) => [t.taskId, t.artistName] as const));

  // Upcoming-events strip — the next 14 days, sorted by date ascending (the server windows; the FE re-sorts).
  const upcoming = [...(events.data?.events ?? [])].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));

  const isLoading = overview.isLoading || attention.isLoading;
  const isError = overview.isError || attention.isError;
  function retry() {
    void overview.refetch();
    void attention.refetch();
    void events.refetch();
  }

  const openArtist = (bandId: string | null | undefined) => bandId && onNavigate?.(`/roster/artists/${bandId}`);
  // A to-do row opens the TASK. `onOpenTask` is optional and an unidentified row has no task to open, so
  // either gap falls back to the artist — the row's previous behaviour, kept as the remainder rather than as
  // the rule.
  const openTask = (bandId: string | null | undefined, taskId: string | null | undefined) =>
    bandId && taskId && onOpenTask ? onOpenTask(bandId, taskId) : openArtist(bandId);

  // Born-blank is `artistCount === 0`, as on the Label home and for the same reason: every card here is a
  // roster aggregate. The error carries whichever of the two reads failed.
  const state: RosterScreenState = isLoading
    ? { status: "loading" }
    : isError
      ? { status: "error", error: overview.error ?? attention.error, onRetry: retry }
      : !data || artistCount === 0
        ? {
            status: "empty",
            title: "No artists yet",
            description:
              "Artists appear here once they link your organization and assign you to their team. Nothing to show until then.",
            // Team is `roster:manage_seats`. Without it this was a door onto a permission wall, so a
            // staffer gets the empty state's guidance and no button — the same rule the account menu's
            // Team entry now follows.
            action: canManageTeam ? (
              <Button variant="ghost" onClick={() => openTeamPanel()}>
                <Icon icon={Users} size={16} /> Manage team
              </Button>
            ) : undefined,
          }
        : { status: "ready" };

  return (
    <RosterScreenShell
      // Legacy's h1 is the PAGE ("Roster heartbeat"), with the org in a crumb above it — the crumb is
      // declined here for the same reason the Label all-roster one was (2026-08-17, no eyebrow over an h1),
      // and the org is already named by the rail, the topbar lockup and the account chip.
      title="Roster heartbeat"
      sub={
        <>
          <span>{managedLine(artistCount)}</span>
          {/* The shared staleness primitive. This header used to append the stamp INSIDE the sub-line as
              " · updated 3h ago" off its own `.asOf` class, while Label said "synced 3h ago" and Booking
              "last sync 3h ago" — one fact, three renderings, on three pages of one product. Renders
              nothing without a stamp; legacy hardcodes "not synced yet", which reads as a fault. */}
          <AsOfMarker asOf={data?.header?.lastRefresh} />
        </>
      }
      state={state}
    >
      <>
          {/* NEEDS ATTENTION (ART-324) — one row per artist, their single most pressing item, saying who put it
              there. Awareness, not tasks: a row drops off on its own. Hold and show rows open the show editor. */}
          <NeedsAttentionCard
            scope="roster"
            items={attention.data?.items}
            total={attention.data?.total}
            canOpenShows
            onOpenArtist={(bandId) => openArtist(bandId)}
            onEdited={() => void attention.refetch()}
          />

          <div className={styles.twoUp}>
            {/* ROSTER PULSE — RANKED, which is what its sub-line claims and why the row leads with a
                position rather than the sparkline the Label lens's pulse carries. Both cards read the same
                growth; this one foregrounds the ORDER, that one the SHAPE. Legacy's "See rankings →" is
                inert (there is no cross-roster rankings surface) and is not ported. */}
            <Card
              id="heartbeat-pulse"
              className={styles.scrollCard}
              title="Roster pulse"
              subtitle="Sorted by % stream growth · last 7 days"
            >
              {pulse.length === 0 ? (
                <p className={styles.emptyLine}>No artists yet, add one to see roster pulse.</p>
              ) : (
                <ul className={styles.rowList}>
                  {pulse.map((rowp, i) => (
                    <li key={rowp.bandId ?? i}>
                      <button type="button" className={styles.pulseRow} onClick={() => openArtist(rowp.bandId)}>
                        <span className={styles.pulseRank}>{i + 1}</span>
                        <ArtistMark name={rowp.name} bandId={rowp.bandId} avatarMediaId={rowp.avatarMediaId} size="sm" />
                        <span className={styles.pulseText}>
                          <span className={styles.pulseName}>{rowp.name}</span>
                          {/* The row's own figure, from its own field — not the sparkline's last point. */}
                          <span className={styles.pulseMeta}>
                            {rowp.streams28d != null ? `${compactNumber(rowp.streams28d)} streams` : "—"} · 28d
                          </span>
                        </span>
                        {/* A born-blank artist (<2 snapshots) gets a dash, never "+0.0%". */}
                        {rowp.growthPct != null ? <StatDelta value={rowp.growthPct} /> : <span className={styles.noDelta}>—</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* TASKS THIS WEEK — the viewer's own cross-roster to-dos. The count is the SERVER's total over
                the active set, never the capped preview beneath it (ART-100).
                Legacy's decorative checkbox is NOT ported: it is a `<span>` with no input and no handler, so
                it reads as a control and is not one. The overdue signal it carried rides the due date, which
                already tints — the same treatment the cross-roster to-dos table uses. */}
            <Card
              id="heartbeat-tasks"
              className={styles.scrollCard}
              title="Tasks this week"
              subtitle={`Across roster · ${tasks?.open ?? 0} open`}
            >
              {(tasks?.items?.length ?? 0) === 0 ? (
                <p className={styles.emptyLine}>No open tasks across your roster.</p>
              ) : (
                // The app's one task row (ART-343), with the artist as the sub-line — the roster home's To-Do idiom.
                <div className={styles.todoBleed}>
                  <ul className={taskRowsClassName(styles.taskList)}>
                    {sortTasksForList((tasks?.items ?? []).map(asTaskSummary)).map((t, i) => (
                      // Opens the TASK (ART-119), not just its artist — see RosterHomeScreen's twin.
                      <TaskListRow
                        key={t.taskId ?? i}
                        task={t}
                        people={taskPeople}
                        sub={artistNameOf.get(t.taskId) ?? null}
                        density="compact"
                        onOpen={() => openTask(t.bandId, t.taskId)}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>

          {/* UPCOMING EVENTS — the next 14 days across the roster. Legacy's "Calendar →" is inert; the
              roster has no cross-roster calendar route (only per-artist `/roster/artists/{id}/calendar`),
              so it is not ported either. */}
          <Card
            id="heartbeat-events"
            title="Upcoming events · next 14 days"
            subtitle="Shows, releases, content drops + deadlines across roster"
          >
            {upcoming.length === 0 ? (
              <p className={styles.emptyLine}>No events in the next 14 days.</p>
            ) : (
              <ul className={styles.rowList}>
                {upcoming.map((e, i) => (
                  <li key={e.refId ?? i}>
                    <button type="button" className={styles.eventRow} onClick={() => openArtist(e.bandId)}>
                      <span className={styles.eventDate}>{formatDay(e.date)}</span>
                      <span className={styles.eventKind} data-kind={e.kind ?? undefined}>{e.kind}</span>
                      <span className={styles.eventText}>
                        <span className={styles.eventArtist}>{e.artistName}</span>
                        {e.title ? ` · ${e.title}` : ""}
                        {e.city ? ` · ${e.city}` : ""}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
      </>
    </RosterScreenShell>
  );
}
